import { z } from 'zod';
import type {
    CollaboratorType,
    RoleType,
    ShirtSize,
    StatusType,
    PcdType,
    SexType,
    RaceType,
    MaritalStatusType,
    HealthInsuranceProviderType,
    FeedbackType,
    CollaboratorPrivate
} from './collaborator.types';

// ================================================
// SCHEMAS ZOD - VALIDAÇÕES
// ================================================

// Schemas básicos
export const CollaboratorTypeSchema = z.enum(['Celetista', 'Fornecedor', 'Sócio', 'Estagiário']);
export const RoleTypeSchema = z.enum(['administrative', 'operational']);
export const ShirtSizeSchema = z.enum(['P', 'M', 'G', 'GG', 'GGG', 'GGGG', 'Outro']);
export const StatusTypeSchema = z.enum(['PENDING', 'UPDATED', 'IN_PROGRESS']);
export const PcdTypeSchema = z.enum(['Física', 'Auditiva', 'Visual', 'Mental', 'Intelectual']);
export const SexTypeSchema = z.enum(['Masculino', 'Feminino', 'Outro']);
export const RaceTypeSchema = z.enum(['Branca', 'Preta', 'Parda', 'Amarela', 'Indígena', 'Outra']);
export const MaritalStatusTypeSchema = z.enum(['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável']);
export const HealthInsuranceProviderTypeSchema = z.enum(['Bradesco', 'Unimed', 'Noterdame', 'Outro']);
export const FeedbackTypeSchema = z.enum(['occurrence', 'raro', 'client']);
export const WorkModelPreferenceSchema = z.enum(['Remote', 'Hybrid', 'Onsite']);

// Schemas de objetos aninhados
export const ChildSchema = z.object({
    name: z.string().optional(),
    sex: SexTypeSchema,
    birthDate: z.string(),
    // Documentos do filho
    birthCertificateUrl: z.string().optional(),
    identityDocumentUrl: z.string().optional(),
});

export const HealthDependentSchema = z.object({
    name: z.string(),
    relationship: z.enum(['Cônjuge', 'Filho(a)']),
    isCovered: z.boolean(),
    cardNumber: z.string().optional(),
    age: z.number().optional(),
});

export const EmergencyContactSchema = z.object({
    name: z.string(),
    phone: z.string(),
    relationship: z.string(),
});

export const VacationPeriodSchema = z.object({
    id: z.string().optional(),
    startDate: z.string(),
    endDate: z.string(),
    days: z.number().optional(),
    observations: z.string().optional(),
    hasSellDays: z.boolean().optional(),
});

export const PaidRestPeriodSchema = z.object({
    id: z.string().optional(),
    startDate: z.string(),
    endDate: z.string(),
    days: z.number().optional(),
    observations: z.string().optional(),
});

export const MedicalCertificateSchema = z.object({
    id: z.string().optional(),
    date: z.string(),
    cid: z.string(),
    days: z.number(),
    certificateUrl: z.string().optional(),
});

export const IdentityDocumentSchema = z.object({
    number: z.string().optional(),
    issuer: z.string().optional(),
    uf: z.string().optional(),
    issueDate: z.string().optional(),
    idDocumentFrontUrl: z.string().optional(),
    idDocumentBackUrl: z.string().optional(),
});

export const ResourceSchema = z.object({
    typeId: z.string(),
    technologyId: z.string(),
    seniorityId: z.string(),
    isPrimary: z.boolean(),
});

export const UpdateStatusEntrySchema = z.object({
    byUser: z.object({
        status: StatusTypeSchema,
        currentStep: z.number().optional(),
    }).optional(),
    byDHO: z.object({ status: StatusTypeSchema }).optional(),
    byDP: z.object({ status: StatusTypeSchema }).optional(),
    byTech: z.object({ status: StatusTypeSchema }).optional(),
});

export const SalaryEntrySchema = z.object({
    id: z.string().optional(),
    date: z.string(),
    amount: z.number(),
    extras: z.number().optional(),
    salaryType: z.string(),
    reason: z.string(),
    observations: z.string().optional(),
});

export const FeedbackEntrySchema = z.object({
    id: z.string().optional(),
    date: z.string(),
    description: z.string(),
    type: FeedbackTypeSchema,
    severity: z.enum(['low', 'medium', 'high']).optional(),
    resolved: z.boolean().optional(),
});

export const EducationEntrySchema = z.object({
    id: z.string().optional(),
    type: z.enum(['Ensino Médio', 'Graduação', 'Pós-graduação', 'Mestrado', 'Doutorado']),
    course: z.string().optional(),
    status: z.string().optional(),
    institution: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    diplomaUrl: z.string().optional(),
});

// Schemas externos (importados de outros módulos)
export const AddressSchema = z.object({
    fullAddress: z.string().optional(),
    number: z.string().optional(),
    complement: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    proofOfAddressUrl: z.string().optional(),
});

export const BankAccountSchema = z.object({
    bankCode: z.string().optional(),
    agency: z.string().optional(),
    account: z.string().optional(),
    digit: z.string().optional(),
    pixKey: z.string().optional(),
});

// Schema para resumo de férias do colaborador
export const NextVacationDataSchema = z.object({
    requestId: z.string(),
    status: z.enum([
        'Draft',
        'PendingManager',
        'PendingClient',
        'PendingDP',
        'Scheduled',
        'OnVacation',
        'Completed',
        'Rejected',
    ]),
    startDate: z.string(),
    endDate: z.string(),
    conflictAllocationIds: z.array(z.string()),
    conflictClientName: z.string().nullable().optional(),
    conflictProjectName: z.string().nullable().optional(),
});

export const CollaboratorVacationSummarySchema = z.object({
    eligible: z.boolean().optional(),
    ineligibilityReason: z
        .enum(['ContractType', 'InactiveStatus', 'MissingAdmissionDate'])
        .nullable()
        .optional(),
    balanceDays: z.number().optional(),
    expiredDays: z.number().optional(),
    scheduledDays: z.number().optional(),
    nextDeadline: z.string().nullable().optional(),
    concessivePeriod: z.object({ start: z.string(), end: z.string() }).nullable().optional(),
    entitledFrom: z.string().nullable().optional(),
    nextVacation: NextVacationDataSchema.nullable().optional(),
    updatedAt: z.union([z.date(), z.string(), z.null()]).optional(),
});

// ================================================
// SCHEMA PRINCIPAL - COLABORADOR PRIVATE
// ================================================

export const CollaboratorPrivateSchema = z.object({
    // === Identificação Básica ===
    id: z.string(),
    name: z.string(),
    photoUrl: z.string().optional(),

    // === Status e Controle ===
    status: z.string().optional(),
    email: z.string().optional(), // Email corporativo
    updatedStatus: UpdateStatusEntrySchema.optional(),

    // === Dados Pessoais ===
    personalEmail: z.string().email('Email inválido').optional(),
    phone: z.string().optional(),
    birthDate: z.string().optional(),
    nationality: z.string().optional(),
    naturality: z.string().optional(),
    birthState: z.string().optional(),
    sex: SexTypeSchema.optional(),
    gender: z.string().optional(),
    race: RaceTypeSchema.optional(),

    // === Endereços ===
    addresses: z.array(AddressSchema).optional(),
    isMailingSameAsMain: z.boolean().optional(),

    // === Contratação ===
    collaboratorType: CollaboratorTypeSchema.optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    terminationReason: z.string().nullable().optional(),
    terminationAllocationId: z.string().nullable().optional(),
    experienceEndDate: z.string().optional(),
    workSchedule: z.number().min(1).max(24).optional(),
    preferredWorkModels: z.array(WorkModelPreferenceSchema).nullable().optional(),

    // === Posição e Hierarquia ===
    positionId: z.string().optional(),
    positionName: z.string().optional(),
    seniorityId: z.string().optional(),
    seniorityName: z.string().optional(),
    roleType: RoleTypeSchema.optional(),

    // === Responsabilidades ===
    responsibleId: z.string().optional(),
    responsibleName: z.string().optional(),
    departmentId: z.string().optional(),
    department: z.string().optional(),
    departmentName: z.string().optional(),
    clientResponsible: z.string().optional(),

    // === Características Profissionais ===
    isRaroAcademyAlumnus: z.boolean().optional(),

    // === Fornecedor ===
    cnpj: z.string().optional(),
    companyName: z.string().optional(),

    // === Estagiário ===
    internshipEducationalInstitution: z.string().optional(),
    internshipCourse: z.string().optional(),
    internshipEndDate: z.string().optional(),
    internshipContractUrl: z.string().optional(),

    // === Capacidades Técnicas ===
    resources: z.array(ResourceSchema).optional(),
    rating: z.number().min(1).max(10).optional(),

    // === Família ===
    motherName: z.string().optional(),
    fatherName: z.string().optional(),
    maritalStatus: MaritalStatusTypeSchema.optional(),
    spouseName: z.string().optional(),
    children: z.array(ChildSchema).optional(),

    // === Contato de Emergência ===
    emergencyContact: EmergencyContactSchema.optional(),

    // === Saúde ===
    hasHealthInsurance: z.boolean().optional(),
    healthInsuranceProvider: HealthInsuranceProviderTypeSchema.optional(),
    healthInsuranceInclusionDate: z.string().optional(),
    healthInsuranceCardNumber: z.string().optional(),
    healthInsurancePlan: z.string().optional(),
    healthInsuranceExtraInfo: z.string().optional(),
    healthDependents: z.array(HealthDependentSchema).optional(),
    healthPlanIdentifiers: z.record(z.string(), z.string()).optional(),
    admissionExamUrl: z.string().optional(),
    terminationExamUrl: z.string().optional(),
    medicalCertificates: z.array(MedicalCertificateSchema).optional(),
    isPCD: z.boolean().optional(),
    pcdTypes: z.array(PcdTypeSchema).optional(),

    // === Remuneração e Benefícios ===
    salaryAccount: BankAccountSchema.optional(),
    personalAccount: BankAccountSchema.optional(),
    salaryHistorySalaries: z.array(SalaryEntrySchema).optional(),
    hasCajuBenefit: z.boolean().optional(),
    hasWellhubBenefit: z.boolean().optional(),
    hasZeloBenefit: z.boolean().optional(),
    zeloDependents: z.array(z.string()).optional(),
    hasGuapecoBenefit: z.boolean().optional(),
    transportationBenefitAmount: z.number().min(0).optional(),
    vacationPeriods: z.array(VacationPeriodSchema).optional(),
    hasPaidRest: z.boolean().optional(),
    paidRestObservations: z.string().optional(),
    paidRestPeriods: z.array(PaidRestPeriodSchema).optional(),

    // === Férias ===
    vacationSummary: CollaboratorVacationSummarySchema.optional(),

    // === Documentos ===
    cpf: z.string().optional(),
    esocialCode: z.string().optional(),
    identityDocument: IdentityDocumentSchema.optional(),
    idDocumentFrontUrl: z.string().optional(),
    idDocumentBackUrl: z.string().optional(),
    workContractUrl: z.string().optional(),

    // === Documentos Familiares ===
    marriageCertificateUrl: z.string().optional(),
    spouseIdentityUrl: z.string().optional(),
    motherIdentityUrl: z.string().optional(),
    fatherIdentityUrl: z.string().optional(),

    // === Preferências Pessoais ===
    shirtSize: ShirtSizeSchema.optional(),
    slipperSize: z.string().optional(),
    hasOnboardingKit: z.boolean().optional(),
    hasBackpack: z.boolean().optional(),
    freeTimeActivities: z.string().optional(),
    favoriteFoodAndDrink: z.string().optional(),
    favoriteColor: z.string().optional(),
    favoriteBook: z.string().optional(),
    favoriteMovieOrSeries: z.string().optional(),
    favoriteCharacter: z.string().optional(),
    sportsPractice: z.string().optional(),
    specialTalent: z.string().optional(),
    futureGoals: z.string().optional(),
    generalObservations: z.string().optional(),

    // === Feedbacks ===
    occurrences: z.array(FeedbackEntrySchema).optional(),
    raroFeedbacks: z.array(FeedbackEntrySchema).optional(),
    clientFeedbacks: z.array(FeedbackEntrySchema).optional(),

    // === Formação Acadêmica ===
    educations: z.array(EducationEntrySchema).optional(),

    // === Metadados ===
    _metadata: z.object({
        createdAt: z.date().optional(),
        updatedAt: z.date().optional(),
        version: z.number().optional(),
        createdBy: z.string().optional(),
        updatedBy: z.string().optional(),
    }).optional(),
});

// ================================================
// SCHEMAS CONTEXTUAIS
// ================================================

// Schema para contexto PROFILE_UPDATE (campos editáveis pelo usuário)
export const CollaboratorSelfSchema = CollaboratorPrivateSchema.pick({
    id: true,
    name: true,
    photoUrl: true,
    personalEmail: true,
    phone: true,
    birthDate: true,
    nationality: true,
    naturality: true,
    birthState: true,
    sex: true,
    gender: true,
    race: true,
    addresses: true,
    isMailingSameAsMain: true,
    maritalStatus: true,
    motherName: true,
    fatherName: true,
    spouseName: true,
    children: true,
    emergencyContact: true,
    isPCD: true,
    pcdTypes: true,
    salaryAccount: true,
    personalAccount: true,
    cpf: true,
    identityDocument: true,
    idDocumentFrontUrl: true,
    idDocumentBackUrl: true,
    shirtSize: true,
    slipperSize: true,
    freeTimeActivities: true,
    favoriteFoodAndDrink: true,
    favoriteColor: true,
    favoriteBook: true,
    favoriteMovieOrSeries: true,
    favoriteCharacter: true,
    sportsPractice: true,
    specialTalent: true,
    futureGoals: true,
});

// Schema para dados públicos da organização
export const CollaboratorPublicSchema = CollaboratorPrivateSchema.pick({
    id: true,
    name: true,
    email: true,
    status: true,
    photoUrl: true,
    phone: true,
    positionId: true,
    positionName: true,
    responsibleId: true,
    responsibleName: true,
    departmentId: true,
    departmentName: true,
    clientResponsible: true,
    isRaroAcademyAlumnus: true,
    resources: true,
}).extend({
    city: z.string().optional(),
    state: z.string().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    allocationManagerUntil: z.string().nullish(),
});

// ================================================
// TYPES PARA COMPATIBILIDADE
// ================================================

// Tipos derivados dos schemas
export type CollaboratorPrivateInput = z.input<typeof CollaboratorPrivateSchema>;
export type CollaboratorPrivateOutput = z.output<typeof CollaboratorPrivateSchema>;
export type CollaboratorSelfInput = z.input<typeof CollaboratorSelfSchema>;
export type CollaboratorSelfOutput = z.output<typeof CollaboratorSelfSchema>;
export type CollaboratorPublicInput = z.input<typeof CollaboratorPublicSchema>;
export type CollaboratorPublicOutput = z.output<typeof CollaboratorPublicSchema>;

// Alias para compatibilidade
export const CollaboratorSchema = CollaboratorPrivateSchema;
export const BaseCollaboratorSchema = CollaboratorPrivateSchema; 
